import { useStoreHealth } from '../api/coverage';
import type { StoreHealth } from '../api/coverage';

/** What went wrong with one store's last read, in one sentence.
 *
 * Returns '' for a run worth saying nothing about, which is the common case:
 * this banner has to be invisible on a normal week or it stops being read.
 */
function complaint(row: StoreHealth): string {
  if (row.status === 'never') {
    return `${row.store} has never been read, so nothing can be planned from it.`;
  }
  if (row.status === 'failed') {
    return `${row.store} could not be read, so its prices are from the last refresh that worked.`;
  }
  if (row.status === 'partial') {
    // Named separately from a plain partial because it is the failure that
    // looks most like success -- the prices arrived, the matches did not.
    if (row.candidates_unjudged > 0) {
      return `${row.store} was read, but ${row.candidates_unjudged} product ${
        row.candidates_unjudged === 1 ? 'match' : 'matches'
      } could not be checked and were left out.`;
    }
    return `${row.store} was only partly read.`;
  }
  if (row.status === 'success' && row.candidates_unjudged > 0) {
    return `${row.store} left ${row.candidates_unjudged} product matches unchecked.`;
  }
  return '';
}

function when(finished: string | null): string {
  if (!finished) return '';
  return new Date(finished).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Tells the cook when a shop's prices are not what they look like.
 *
 * The refreshes run overnight with nobody watching, so a shop that started
 * refusing us is otherwise silent: the week still generates, the prices are
 * just old. Deliberately not an error state — the plan is still usable, and
 * saying so loudly every week would train it to be ignored.
 */
export default function RefreshHealth() {
  const { data } = useStoreHealth();
  if (!data) return null;

  const problems = data
    .map((row) => ({ row, message: complaint(row) }))
    .filter((entry) => entry.message);
  if (problems.length === 0) return null;

  return (
    <div className="card notice notice--blocked" role="status">
      {problems.map(({ row, message }) => (
        <p key={row.store_id}>
          {message}
          {row.finished_at && (
            <span className="muted small"> Last read {when(row.finished_at)}.</span>
          )}
        </p>
      ))}
      <p className="muted small">
        Nothing here stops you cooking this week — it means one shop’s prices are
        older than the others’.
      </p>
    </div>
  );
}
